/**
 * The plan list, from the server.
 *
 * Both list screens use this one hook — the plan list with no `client`, a
 * client's Plans tab with theirs. The tab and the search box are filters the
 * server applies, so changing either refetches rather than narrowing what is
 * already on screen: the list is paginated, and filtering a page in the
 * browser would only ever search the rows that page happened to hold.
 *
 * The page is the server's too. A screen asks for one page's worth and gets
 * exactly that, plus the `count` of everything the filters match — which is
 * what it divides into pages to draw the control with. Turning a page is
 * therefore another call, the same as typing in the search box is.
 *
 * Which page is the caller's to hold, not this hook's: it is the thing a
 * screen has to reset when a filter changes, and the hook has no way of
 * knowing that a filter came from the user rather than from a re-render.
 */

import { useEffect, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { DEFAULT_PAGE_SIZE, fetchMealPlans } from "../services/mealPlansService";
import type { PlanStatus, PlanSummary } from "../types/plans.types";

/** Long enough that typing a client's name is one request, not eight. */
const SEARCH_DEBOUNCE_MS = 300;

export type MealPlansStatus = "loading" | "ready" | "failed";

export interface MealPlansState {
  /** One page of them — `pageSize` rows at most, not the whole list. */
  plans: PlanSummary[];
  /** How many the server holds for these filters, not how many came back. */
  count: number;
  /** Whether there is a page after this one, as the response reported it. */
  hasNext: boolean;
  status: MealPlansStatus;
  /** Set only when `status` is `failed` — what the screen shows. */
  error?: string;
  /**
   * Whether the server refused the read outright rather than failing to answer
   * it. This endpoint answers 403 to a workspace with no plan running, which is
   * the ordinary state of one before its first purchase — not an error the
   * reader can do anything about, and not news either: a practice that has
   * never been able to write a meal plan has never written one.
   *
   * The screen draws it as the empty list it is. See `CreatePlanPage`.
   */
  refused: boolean;
  /** Runs the call again. For a Try again button, not for every keystroke. */
  reload: () => void;
}

export interface UseMealPlansOptions {
  /** A client's `public_id`. Absent asks for the whole workspace. */
  client?: string;
  /** `"all"` leaves the filter off, which asks for drafts and sent alike. */
  status?: PlanStatus | "all";
  /** Debounced here, so a call site can pass the box's value directly. */
  search?: string;
  /** 1-based, the way the endpoint counts. Changing it refetches. */
  page?: number;
  /** How many rows a page holds. Defaults to the service's ten. */
  pageSize?: number;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

const NO_PLANS: PlanSummary[] = [];

export function useMealPlans({
  client,
  status = "all",
  search = "",
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseMealPlansOptions = {}): MealPlansState {
  const getToken = useTokenGetter();

  // Only the search is debounced: the tab and the client are picked, not
  // typed, so making either wait would just feel slow.
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const isFirstSearch = useRef(true);

  useEffect(() => {
    // The first pass must not wait — the screen would otherwise open on a
    // blank list for no reason, since there is nothing typed to debounce.
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      setDebouncedSearch(search);
      return;
    }

    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  const query = useQuery({
    queryKey: [
      "plans",
      "list",
      client ?? null,
      status,
      debouncedSearch,
      page,
      pageSize,
    ] as const,
    queryFn: ({ signal }) =>
      fetchMealPlans(getToken, {
        client,
        status,
        search: debouncedSearch,
        page,
        pageSize,
        signal,
      }),
    // The rows already on screen stay while the next set is fetched — cached
    // across mounts too, so a list visited again shows its last page instantly
    // while a fresh read runs behind it.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.isError) console.error("Meal plans failed to load:", query.error);
  }, [query.isError, query.error]);

  // 403 is this endpoint's answer to a workspace with no plan running.
  const refused = query.error instanceof ApiError && query.error.status === 403;

  return {
    // Back to nothing on a real failure, same as before: a row from a page
    // that is no longer confirmed current is worse than none.
    plans: query.isError ? NO_PLANS : query.data?.plans ?? NO_PLANS,
    count: query.isError ? 0 : query.data?.count ?? 0,
    hasNext: query.isError ? false : query.data?.hasNext ?? false,
    status: query.isError ? "failed" : query.isFetching ? "loading" : "ready",
    error: query.isError ? failureMessage(query.error) : undefined,
    refused: query.isError && refused,
    reload: () => {
      void query.refetch();
    },
  };
}
